import React, { useState, useEffect } from 'react';
import { Card, Form, Table, Alert, Modal, Button } from 'react-bootstrap';
import { getDataReader } from '../../livedataFuncs';
import { useAppSelector } from '../../redux/hooks';
import { lddStateObject } from '../../redux/reducer';
import styles from './featureToolbar.module.scss';
import { runInnerFunction } from '../../compatManager';
import { getLocalDateInt, determineTaxBilled, resolveTaskId } from './solunoHelpers';

interface FinancialDetailsProps {
    staticPreLoad: any;
    headers: any;
    linkedCasefileId: string;
    casefileNumber: string;
    defaultTaskId: string | null;
}

interface StaffMember {
    id: string;
    fullName: string;
}

// Add a type for the export log entries
type ExportLogEntry = {
    message: string;
    details: Record<string, string>;
    type?: 'success' | 'warning' | 'info'; // Default to 'success' if not specified
};

// Add interface for modal props
interface ExportLogModalProps {
    show: boolean;
    onHide: () => void;
    exportData?: Record<string, string>;
}

const FinancialDetails: React.FC<FinancialDetailsProps> = ({ staticPreLoad, headers, linkedCasefileId, casefileNumber, defaultTaskId }) => {
    const livedataRootContainer = useAppSelector((state: lddStateObject) => state.currentNavigation.livedataRootContainer);
    const DataReaderObj = getDataReader(livedataRootContainer.dataRoot);
    const appCode = DataReaderObj.getAppCode();
    
    // Get GST object from DataReader
    const gstObject = DataReaderObj.getGSTObject(appCode);
    
    const [solunoData, setSolunoData] = useState(DataReaderObj.getSolunoData(appCode));
    
    // Use solunoData.taskData instead of directly accessing DataReaderObj
    const expenses = solunoData.taskData;
    const [includeClientInfo, setIncludeClientInfo] = useState<boolean>(true);
    const [includeFees, setIncludeFees] = useState<boolean>(true);
    const [includeDisbursements, setIncludeDisbursements] = useState<boolean>(true);
    const [includeNonHstDisbursements, setIncludeNonHstDisbursements] = useState<boolean>(true);
    const contactInfo = solunoData.contactInfo;
    const taskData = solunoData.taskData;  // this is the fees/ disbursments, etc.
    const [clientFields, setClientFields] = useState([
        { id: 'fullName', label: 'Full Name *', value: contactInfo.fullName },
        { id: 'email', label: 'Email *', value: contactInfo.email },
        { id: 'mailingAddress', label: 'Mailing Address', value: [
            contactInfo.mailingAddress,
            contactInfo.city,
            contactInfo.province,
            contactInfo.postalCode,
            'Canada'
        ].filter(Boolean).join(', ') },
        { id: 'cellPhone', label: 'Cell Phone', value: '' }
    ])
    const [editableValues, setEditableValues] = useState<{ [key: string]: string }>(
        clientFields.reduce((acc, field) => ({
            ...acc,
            [field.id]: field.value || ''
        }), {})
    );

    const [staffList, setStaffList] = useState<StaffMember[]>([]);
    const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
    const [exportLog, setExportLog] = useState<ExportLogEntry[]>([]);

    // Add new state for editable code types
    const [editableCodeTypes, setEditableCodeTypes] = useState<{ [key: string]: string }>(
        expenses.reduce((acc, item) => ({
            ...acc,
            [item.Description]: item.CodeType || ''
        }), {})
    );

    // Add new state for modal
    const [showModal, setShowModal] = useState(false);
    const [selectedExport, setSelectedExport] = useState<any>(null);
    const [disableSend, setDisableSend] = useState(false);

    // Add new state for export log modal
    const [showExportLogModal, setShowExportLogModal] = useState(false);

    // Update component definition
    const ExportLogModal = ({ show, onHide }: ExportLogModalProps) => (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <h2>Soluno Financial Export</h2>
            </Modal.Header>
            <Modal.Body>
                {exportLog.length === 0 ? (
                    <Alert variant="info">No information has been exported to Soluno yet.</Alert>
                ) : (
                    [...exportLog].reverse().map((log, index) => (
                        <Alert
                            key={index}
                            variant={log.type || 'success'}
                            onClick={() => {
                                setSelectedExport(log.details);
                                setShowModal(true); // Open the details modal
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            {log.message} <a>View Details</a>
                        </Alert>
                    ))
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="link" onClick={onHide} style={{marginRight: 'auto'}}>Close</Button>
                <div></div>
            </Modal.Footer>
        </Modal>
    );

    useEffect(() => {
        const mappedexpenses = taskData.map(task => {
            const matchedTask = staticPreLoad.ActivityCodes.find(
                preloadTask => preloadTask.Name === task.CodeType
            );

            return {
                ...task,
                id: matchedTask ? matchedTask.Id : null,
                GLAccountID: matchedTask ? matchedTask.GLAccountId : null
            };
        });

        setSolunoData(prev => ({
            ...prev,
            taskData: mappedexpenses
        }));
    }, []);

    useEffect(() => {
        if (staticPreLoad?.Users) {
            const staff = staticPreLoad.Users
                .filter(user => user.CanRecordTime === true)
                .map(user => ({
                    id: user.Id,
                    fullName: user.FullName
                }));
            setStaffList(staff);
        }
    }, []);

    const feesData = expenses.filter(item => item.ItemType === 'Fee');
    const disbursementsData = expenses.filter(item => item.ItemType === 'Disbursement');

    function formatName(fullName: string): string {
        const nameParts = fullName.split(' ');
    
        if (nameParts.length < 2) return fullName; // Return as is if there's no space
    
        const [firstName, ...rest] = nameParts;
        const lastName = rest.pop(); // Get the last element as the last name
        const middleName = rest.join(' '); // Join the remaining parts as the middle name
    
        return middleName ? `${lastName}, ${firstName} ${middleName}` : `${lastName}, ${firstName}`;
    }

    /** Server-side search by formatted display name (avoids truncated full contact list). */
    async function findExistingContactId(): Promise<string | null> {
        try {
            const fullName = (editableValues['fullName'] ?? clientFields[0].value ?? '').trim();
            const formattedName = formatName(fullName);
            const response = await fetch(
                `https://cloud.soluno.legal/api/data/Contacts?search=${encodeURIComponent(formattedName)}&count=1&incDeleted=false&incActive=true`,
                { method: 'GET', headers }
            );
            if (!response.ok) {
                return null;
            }
            const data = await response.json();
            if (!Array.isArray(data) || data.length === 0) {
                return null;
            }
            const contact = data[0];
            return contact.Id ?? null;
        } catch (error) {
            console.error('Error searching for contact:', error);
            return null;
        }
    }

    async function getNextAvailableContactNumber(headers: any) {
        try {
            const response = await fetch('https://cloud.soluno.legal/api/data/Contacts?search=_NEXTAVAIL_&count=0', {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return String(parseInt(data[0].ContactNumber) + 1);
        } catch (error) {
            console.error('Error getting next available contact number:', error);
            throw error;
        }
    }

    async function createContact(clientFields: any) {
        try {
            const nextContactNumber = await getNextAvailableContactNumber(headers);
            const formattedName = formatName(clientFields[0].value);
            const [lastName, firstName] = formattedName.split(',').map(part => part.trim());

            // Parse the mailing address
            const addressParts = clientFields[2]?.value?.split(',').map(part => part.trim()) || [];
            const [streetAddress, city, province, postalCode, country] = addressParts;

            const contactData = {
                Language: "en",
                ContactNumber: nextContactNumber,
                Addresses: [{
                    AddressType: "Individual",
                    First: firstName || "",
                    Last: lastName || "",
                    Address1: streetAddress || "",
                    City: city || "",
                    State: province || "",
                    Country: country || "Canada",
                    Zip: postalCode || "",
                    AddressUsage: "Main"
                }],
                Phones: [
                    ...(clientFields[3]?.value ? [{
                        PhoneType: "WorkMobile", 
                        Value: clientFields[3].value,
                        Description: "",
                        BillingEmail: false
                    }] : []),
                    ...(clientFields[1]?.value ? [{
                        PhoneType: "WorkEmail",
                        Value: clientFields[1].value,
                        Description: "",
                        BillingEmail: false
                    }] : [])
                ]
            };

            const response = await fetch('https://cloud.soluno.legal/api/data/Contacts', {
                method: 'POST',
                headers: {
                    ...headers,
                },
                body: JSON.stringify(contactData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.ContactId; // Return just the ContactId instead of the full response

        } catch (error) {
            console.error('Error creating contact:', error);
            throw error;
        }
    }

    async function exportClientInfo(
        linkedCasefileId: string,
        casefileNumber: string,
        contactId: string,
        staffId: string,
        headers: any
    ) {
        try {
            const casefileResponse = await fetch(`https://cloud.soluno.legal/api/data/Casefiles?id=${linkedCasefileId}`, {
                method: 'GET',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                }
            });

            if (!casefileResponse.ok) {
                throw new Error(`Failed to fetch casefile before export. HTTP error! status: ${casefileResponse.status}`);
            }

            const currentCasefile = await casefileResponse.json();
            const body = {
                ...currentCasefile,
                Id: linkedCasefileId,
                CasefileNumber: casefileNumber,
                ContactId: contactId,
                Description: "Updated client information",
                Staff: [
                    {
                        UserId: staffId,
                        Responsible: true,
                        Assigned: true,
                        Lead: true
                    }
                ]
            };
    
            const response = await fetch(`https://cloud.soluno.legal/api/data/Casefiles/${linkedCasefileId}`, {
                method: 'POST',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            });
    
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            console.log('Successfully exported client info to Soluno');
        } catch (error) {
            console.error('Error exporting client info to Soluno:', error);
            throw error;
        }
    }
    async function exportExpenses(
        expenses: any[],
        isHstDisbursements: boolean
    ) {
        try {
            // Map expenses to required format
            const formattedExpenses = expenses.map(expense => ({
                ActivityId: expense.actId,
                CasefileId: linkedCasefileId,
                Amount: parseFloat(expense.amount),
                TaxBilled: determineTaxBilled(isHstDisbursements, expense.OtherTaxType),
                Explanation: expense.label,
                EntryType: "Expense", 
                EntryDate: getLocalDateInt()
            }));

            formattedExpenses.forEach((exp, i) => {
                if (!exp.ActivityId) {
                    console.warn(`[Soluno Export] Expense[${i}] "${exp.Explanation}" has no ActivityId — Soluno may silently drop this item`);
                }
            });

            const response = await fetch('https://cloud.soluno.legal/api/data/Expense?responsemode=WebSockets', {
                method: 'POST',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formattedExpenses)
            });

            if (!response.ok) {
                const responseBody = await response.text();
                throw new Error(`HTTP error! status: ${response.status} body: ${responseBody}`);
            }
        } catch (error) {
            console.error('Error exporting expenses to Soluno:', error);
            throw error;
        }
    }
    async function exportFees(
        fees: any[],
        staffId: string,
    ) {
        try {
            // Map expenses to required formatresolveTaskId
            const formattedFees = fees.map(fee => ({
                ActivityId: fee.id,
                FeeEarnerId: staffId,
                CasefileId: linkedCasefileId,
                Amount: parseFloat(fee.Amount),
                Explanation: fee.Description,
                EntryType: "Time", 
                EntryDate: getLocalDateInt(),
                TaskId: resolveTaskId(defaultTaskId, staticPreLoad.Tasks || []) 
            }));
    
            formattedFees.forEach((fee, i) => {
                if (!fee.ActivityId) {
                    console.warn(`[Soluno Export] Fee[${i}] "${fee.Explanation}" has no ActivityId — Soluno may silently drop this item`);
                }
                if (!fee.TaskId) {
                    console.warn(`[Soluno Export] Fee[${i}] "${fee.Explanation}" has no TaskId — Soluno may silently drop this item`);
                }
            });

            const response = await fetch('https://cloud.soluno.legal/api/data/Time?responsemode=WebSockets', {
                method: 'POST',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formattedFees)
            });

            if (!response.ok) {
                const responseBody = await response.text();
                throw new Error(`HTTP error! status: ${response.status} body: ${responseBody}`);
            }
        } catch (error) {
            console.error('Error exporting fees to Soluno:', error);
            throw error;
        }
    }

    async function handleSendToSoluno() {
        try {
            // Add validation for required fields if client info is included
            if (includeClientInfo) {
                const nameValue = editableValues['fullName']?.trim();
                const emailValue = editableValues['email']?.trim();
                
                if (!nameValue && !emailValue) {
                    alert('Name and Email are required fields for exporting client information. Please make sure to fill out these fields');
                    return;
                } else if (!nameValue) {
                    alert('Name is a required field for exporting client information. Please make sure to fill out this field');
                    return;
                } else if (!emailValue) {
                    alert('Email is a required field for exporting client information. Please make sure to fill out this field');
                    return;
                }
            }

            const staffId = selectedStaff?.id;

            // For fees, we don't need to filter by GLAccountID
            const selectedFees = feesData.filter(fee => fee.ExportToSoluno === '1');

            // Get all invalid disbursements that are selected for export
            const invalidHstDisbursements = disbursementsData
                .filter(disbursement => 
                    disbursement.IsTaxable === 'Y' && 
                    (disbursement.GLAccountID === '00000000-0000-0000-0000-000000000000' || 
                     disbursement.GLAccountID === null || 
                     disbursement.GLAccountID === undefined) &&
                    disbursement.ExportToSoluno === '1'
                );

            const invalidNonHstDisbursements = disbursementsData
                .filter(disbursement => 
                    disbursement.IsTaxable === 'N' && 
                    (disbursement.GLAccountID === '00000000-0000-0000-0000-000000000000' || 
                     disbursement.GLAccountID === null || 
                     disbursement.GLAccountID === undefined) &&
                    disbursement.ExportToSoluno === '1'
                );

            // If there are any invalid items selected for export, stop and show alert
            if (invalidHstDisbursements.length > 0 || invalidNonHstDisbursements.length > 0) {
                alert('Some items are missing GL Account information. Please check the export log for details.');
                
                // Add warnings for invalid HST disbursements
                invalidHstDisbursements.forEach(disbursement => {
                    setExportLog(prev => [...prev, {
                        message: `${disbursement.Description} does not have an associated GL account. Please add one in Soluno to enter the disbursement.`,
                        details: {
                            Description: disbursement.Description,
                            Amount: `$${disbursement.Amount}`,
                            Status: 'Not Exported - Missing GL Account or incorrect Activity Code'
                        },
                        type: 'warning'
                    }]);
                });

                // Add warnings for invalid non-HST disbursements
                invalidNonHstDisbursements.forEach(disbursement => {
                    setExportLog(prev => [...prev, {
                        message: `${disbursement.Description} does not have an associated GL account. Please add one in Soluno to enter the disbursement.`,
                        details: {
                            Description: disbursement.Description,
                            Amount: `$${disbursement.Amount}`,
                            Status: 'Not Exported - Missing GL Account or incorrect Activity Code'
                        },
                        type: 'warning'
                    }]);
                });

                return; // Stop the export process
            }

            // If we get here, all selected items are valid
            const foundContactId = await findExistingContactId();
            const contactId = foundContactId ?? await createContact(clientFields);

            const selectedHstDisbursements = disbursementsData
                .filter(disbursement => disbursement.IsTaxable === 'Y')
                .filter(disbursement => disbursement.GLAccountID !== '00000000-0000-0000-0000-000000000000')
                .filter(disbursement => disbursement.ExportToSoluno === '1')
                .map(disbursement => ({
                    label: disbursement.Description,
                    amount: disbursement.Amount,
                    activityCode: disbursement.CodeType,
                    actId: disbursement.id,
                    OtherTaxType: disbursement.OtherTaxType

                }));

            const selectedNonHstDisbursements = disbursementsData
                .filter(disbursement => disbursement.IsTaxable === 'N')
                .filter(disbursement => disbursement.GLAccountID !== '00000000-0000-0000-0000-000000000000')
                .filter(disbursement => disbursement.ExportToSoluno === '1')
                .map(disbursement => ({
                    label: disbursement.Description,
                    amount: disbursement.Amount,
                    activityCode: disbursement.CodeType,
                    actId: disbursement.id
                }));

            // Only add client information if included
            if (includeClientInfo) {
                await exportClientInfo(linkedCasefileId, casefileNumber, contactId, staffId, headers);
                setExportLog(prev => [...prev, {
                    message: 'Client information exported successfully',
                    details: {
                        'Client Name': clientFields[0].value,
                        'Client Email': clientFields[1].value,
                    },
                    type: 'success'
                }]);
                setIncludeClientInfo(false);
            }

            // Only add fees if included
            if (includeFees && selectedFees.length > 0) {
                await exportFees(selectedFees, staffId);
                setExportLog(prev => [...prev, {
                    message: `${selectedFees.length} fees exported successfully`,
                    details: selectedFees.reduce((acc, fee) => ({
                        ...acc,
                        [fee.Description]: `$${fee.Amount}`
                    }), {}),
                    type: 'success'
                }]);
                setIncludeFees(false);
            }

            // Only add selected disbursements
            if (includeDisbursements && selectedHstDisbursements.length > 0) {
                await exportExpenses(selectedHstDisbursements, true); // true for HST disbursements
                setExportLog(prev => [...prev, {
                    message: `${selectedHstDisbursements.length} HST disbursements exported successfully`,
                    details: selectedHstDisbursements.reduce((acc, disbursement) => ({
                        ...acc,
                        [disbursement.label]: `$${disbursement.amount}`
                    }), {}),
                    type: 'success'
                }]);
                setIncludeDisbursements(false);
            }
            
            if (includeNonHstDisbursements && selectedNonHstDisbursements.length > 0) {
                await exportExpenses(selectedNonHstDisbursements, false); // false for non-HST disbursements
                setExportLog(prev => [...prev, {
                    message: `${selectedNonHstDisbursements.length} non-HST disbursements exported successfully`,
                    details: selectedNonHstDisbursements.reduce((acc, disbursement) => ({
                        ...acc,
                        [disbursement.label]: `$${disbursement.amount}`
                    }), {}),
                    type: 'success'
                }]);
                setIncludeNonHstDisbursements(false);
            }

            DataReaderObj.setHistoryItem();
            setDisableSend(true);
            alert('Export to Soluno completed successfully!');

        } catch (error) {
            console.error('Error during export:', error);
            setExportLog(prev => [...prev, {
                message: `Error during export: ${error.message}`,
                details: { error: error.message },
                type: 'warning'
            }]);
        }
    }

    function handleValueChange(fieldId: string, value: string) {
        setEditableValues(prev => ({
            ...prev,
            [fieldId]: value
        }));
    }

    function handleBlur(fieldId: string) {
        setClientFields(prev => 
            prev.map(field => 
                field.id === fieldId 
                    ? { ...field, value: editableValues[fieldId] }
                    : field
            )
        );
    }

    function handleStaffChange(event: React.ChangeEvent<HTMLSelectElement>) {
        const staffMember = staffList.find(staff => staff.id === event.target.value);
        setSelectedStaff(staffMember || null);
    }
    
    const handleExportToggle = (description: string) => {
        // Update the XML
        DataReaderObj.setSolunoItemExport(description);
        
        // Get fresh data just for the updated ExportToSoluno values
        const updatedData = DataReaderObj.getSolunoData(appCode);
        
        // Update only the ExportToSoluno values while keeping other data intact
        setSolunoData(prevData => ({
            ...prevData,
            taskData: prevData.taskData.map(item => ({
                ...item,
                ExportToSoluno: updatedData.taskData.find(
                    updatedItem => updatedItem.Description === item.Description
                )?.ExportToSoluno || item.ExportToSoluno
            }))
        }));
    };

    // Modify the handleCodeTypeChange function
    const handleCodeTypeChange = (description: string, value: string) => {
        // Find the matching activity code from staticPreLoad
        const matchedActivity = staticPreLoad.ActivityCodes.find(
            activity => activity.Name === value
        );

        // Update the editableCodeTypes state
        setEditableCodeTypes(prev => ({
            ...prev,
            [description]: value
        }));

        // Update the expenses array with both the new code type and id
        setSolunoData(prev => ({
            ...prev,
            taskData: prev.taskData.map(expense => 
                expense.Description === description 
                    ? { 
                        ...expense, 
                        CodeType: value,
                        id: matchedActivity ? matchedActivity.Id : null,
                        GLAccountID: matchedActivity ? matchedActivity.GLAccountId : null
                      }
                    : expense
            )
        }));
    };
    const columnStyles = {
        checkbox: { width: '5%' },
        description: { width: '35%' },
        amount: { width: '20%' },
        code: { width: '35%' }
    };

    // Initialize selected items based on ExportToSoluno field
    useEffect(() => {
        taskData.reduce((acc, item) => ({
            ...acc,
            [item.Description]: item.ExportToSoluno === '1' || item.ExportToSoluno === 1
        }), {});
    }, []);


    if (!taskData || taskData.length === 0) {
        return (
            <Alert variant="info" className="alert alert-info m-3" role="alert">
                <b>No data available.</b> Please export your data from Account to Client before proceeding with the financial details.
            </Alert>
        );
    }

    return (
        <>
            <div className={styles.toolbar}>
                <h2>Export to Soluno</h2>
                <Button
                    className="btn-link"
                    onClick={() => runInnerFunction('openZoho', 'articles/soluno-accounting-integration')}
                    target="_blank"
                    title="User Guide"
                    style={{ 
                        cursor: 'pointer',
                        border: 'none',
                        background: 'none',
                        textDecoration: 'none',
                        alignContent: 'right',
                        marginLeft: 'auto'
                    }}
                >
                    <i className="fa-solid fa-book"/> User Guide
                </Button>
                <div>
                    {exportLog.length > 0 ? (
                        <Button 
                            className={styles.btnSuccess}
                            onClick={() => setShowExportLogModal(true)}
                        >
                            View Export Log
                        </Button>
                    ):(
                        <Button 
                            className={styles.btnWarning}
                        >
                            No information exported from this session
                        </Button>
                    )}

                    <Button 
                        className={styles.btnPrimary}
                        onClick={handleSendToSoluno}
                        disabled={!selectedStaff || !(includeFees || includeClientInfo || includeDisbursements || includeNonHstDisbursements) || disableSend}
                    >
                        Send to Soluno
                    </Button>
                </div>
            </div>

            {/* Add the Export Log Modal */}
            <ExportLogModal 
                show={showExportLogModal}
                onHide={() => setShowExportLogModal(false)}
            />

            {/* Add the Details Modal */}
            <Modal
                size="lg"
                className="modal-list"
                centered
                show={showModal}
                onHide={() => {
                    setShowModal(false);
                    setSelectedExport(null);
                }}
            >
                <Modal.Header closeButton>
                    <h2>Export Details</h2>
                </Modal.Header>
                <Modal.Body>
                    {selectedExport && (
                        <Table bordered>
                            <tbody>
                                {Object.entries(selectedExport).map(([key, value]) => (
                                    <tr key={key}>
                                        <td><strong>{key}</strong></td>
                                        <td>{value as string}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="link" style={{marginRight: 'auto'}} onClick={() => {
                        setShowModal(false);
                        setSelectedExport(null);
                    }}>
                        Close
                    </Button>
                    <div></div>
                </Modal.Footer>
            </Modal>

            {/* Responsible Staff Card */}
            <Card className="card-list">
                <Card.Header>
                    <h2>Responsible Staff</h2>
                </Card.Header>
                <Card.Body>
                    <Table bordered className="table-input">
                        <thead>
                            <tr>
                                <th style={columnStyles.checkbox}></th>
                                <th style={columnStyles.description}>Field</th>
                                <th>Details in Realti</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td></td>
                                <td>Responsible Staff</td>
                                <td>
                                    <Form.Select
                                        value={selectedStaff?.id || ''}
                                        onChange={handleStaffChange}
                                    >
                                        <option value="">Select Staff Member</option>
                                        {staffList.map(staff => (
                                            <option key={staff.id} value={staff.id}>
                                                {staff.fullName}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* Client Information Card */}
            <Card className="card-list">
                <Card.Header>
                    <h2>Client Information</h2>
                </Card.Header>
                
                <Card.Body>
                    <Table bordered className="table-input">
                        <thead>
                            <tr>
                                <th style={{width:'5%', textAlign: 'center'}}>
                                    <Form.Check 
                                        type="checkbox"
                                        checked={includeClientInfo}
                                        onChange={() => setIncludeClientInfo(!includeClientInfo)}
                                    />
                                </th>
                                <th style={columnStyles.description}>Field</th>
                                <th>Details in Realti</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clientFields.map(field => (
                                <tr key={field.id}>
                                    <td>
                                        <Form.Check 
                                            type="checkbox"
                                            checked={includeClientInfo}
                                            disabled
                                        />
                                    </td>
                                    <td>{field.label}</td>
                                    <td>
                                        <Form.Control
                                            type="text"
                                            value={editableValues[field.id]}
                                            onChange={(e) => handleValueChange(field.id, e.target.value)}
                                            onBlur={() => handleBlur(field.id)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* Fees Card */}
            <Card className="card-list">
                <Card.Header>
                    <h2>Fees</h2>
                </Card.Header>
                <Card.Body>
                    <Table bordered className="table-input">
                        <thead>
                            <tr>
                                <th style={columnStyles.checkbox}></th>
                                <th style={columnStyles.description}>Fee</th>
                                <th style={columnStyles.amount}>Amount</th>
                                <th style={columnStyles.code}>Act Code</th>
                            </tr>
                        </thead>
                        <tbody>
                            {expenses.filter(item => item.ItemType === 'Fee').map(fee => (
                                <tr key={fee.Description}>
                                    <td>
                                        <Form.Check 
                                            type="checkbox"
                                            checked={fee.ExportToSoluno === '1'}
                                            onChange={() => handleExportToggle(fee.Description)}
                                        />
                                    </td>
                                    <td>{fee.Description}</td>
                                    <td>{fee.Amount}</td>
                                    <td>
                                        <Form.Control
                                            type="text"
                                            value={editableCodeTypes[fee.Description] || fee.CodeType}
                                            onChange={(e) => handleCodeTypeChange(fee.Description, e.target.value)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>

                        {/* Summary rows */}
                        <tfoot>
                            {(appCode === "ON" || appCode === "AB") && gstObject?.IncludeGST === '1' && (
                                <tr>
                                    <td></td>
                                    <td>{appCode === "ON" ? "HST (13%)" : "GST (5%)"} </td>
                                    <td>{gstObject?.GSTFees || '0.00'}</td>
                                    <td></td>
                                </tr>
                            )}
                            
                            {/* For other provinces - show GST and/or PST */}
                            {appCode !== "ON" && appCode !== "AB" && gstObject?.IncludeGSTFee === '1' && (
                                <tr>
                                    <td></td>
                                    <td>{appCode === "NS" ? "HST (14%)" : "GST (5%)"} </td>
                                    <td>{gstObject?.GSTFee || '0.00'}</td>
                                    <td></td>
                                </tr>
                            )}
                            
                            {appCode !== "ON" && appCode !== "AB" && appCode !== "NS" && gstObject?.IncludePSTFee === '1' && (
                                <tr>
                                    <td></td>
                                    <td>{appCode === "MB" ? "RST (7%)" : (appCode === "SK" ? "PST (6%)" : "PST (7%)")}</td>
                                    <td>{gstObject?.PSTFee || '0.00'}</td>
                                    <td></td>
                                </tr>
                            )}
                            
                            <tr>
                                <td></td>
                                <td>Total For Export</td>
                                <td>{(expenses
                                            .filter(item => item.ItemType === 'Fee' && item.ExportToSoluno === '1')
                                            .reduce((sum, fee) => sum + parseFloat(fee.Amount), 0) + 
                                            (() => {
                                                if (appCode === "ON" || appCode === "AB") {
                                                    return gstObject?.IncludeGST === '1' ? parseFloat(gstObject?.GSTFees || '0') : 0;
                                                } else {
                                                    let totalTax = 0;
                                                    if (gstObject?.IncludeGSTFee === '1') {
                                                        totalTax += parseFloat(gstObject?.GSTFee || '0');
                                                    }
                                                    if (gstObject?.IncludePSTFee === '1') {
                                                        totalTax += parseFloat(gstObject?.PSTFee || '0');
                                                    }
                                                    return totalTax;
                                                }
                                            })())
                                            .toFixed(2)}
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </Table>
                </Card.Body>
            </Card>

            {/* Other Taxable Charges Card - Only for SK, BC, MB */}
            {(appCode === "SK" || appCode === "BC" || appCode === "MB") && (
                <Card className="card-list">
                    <Card.Header>
                        <h2>Other Taxable Charges</h2>
                    </Card.Header>
                    <Card.Body>
                        <Table bordered className="table-input">
                            <thead>
                                <tr>
                                    <th style={columnStyles.checkbox}></th>
                                    <th style={columnStyles.description}>Description</th>
                                    <th style={columnStyles.amount}>Amount</th>
                                    <th style={columnStyles.code}>Act Code</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expenses
                                    .filter(item => 
                                        item.ItemType === 'Disbursement' && 
                                        ['PST', 'GST', 'None', 'Both'].includes(item.OtherTaxType)
                                    )
                                    .map(disbursement => (
                                        <tr key={disbursement.Description}>
                                            <td>
                                                <Form.Check 
                                                    type="checkbox"
                                                    checked={disbursement.ExportToSoluno === '1'}
                                                    onChange={() => handleExportToggle(disbursement.Description)}
                                                />
                                            </td>
                                            <td>{disbursement.Description}</td>
                                            <td>{disbursement.Amount}</td>
                                            <td>
                                                <Form.Control
                                                    type="text"
                                                    value={editableCodeTypes[disbursement.Description] || disbursement.CodeType}
                                                    onChange={(e) => handleCodeTypeChange(disbursement.Description, e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                            <tfoot>
                                {gstObject?.GSTOnOtherItems && parseFloat(gstObject.GSTOnOtherItems) > 0 && (
                                    <tr>
                                        <td></td>
                                        <td>GST (5%)</td>
                                        <td>{gstObject.GSTOnOtherItems}</td>
                                        <td></td>
                                    </tr>
                                )}
                                {gstObject?.PSTOnOtherItems && parseFloat(gstObject.PSTOnOtherItems) > 0 && (
                                    <tr>
                                        <td></td>
                                        <td>{appCode === "BC" ? "PST (7%)" : (appCode === "SK" ? "PST (6%)" : "RST (7%)")}</td>
                                        <td>{gstObject.PSTOnOtherItems}</td>
                                        <td></td>
                                    </tr>
                                )}
                                <tr>
                                    <td></td>
                                    <td>Total Other Charges</td>
                                    <td>
                                        {(expenses
                                            .filter(item => 
                                                item.ItemType === 'Disbursement' && 
                                                ['PST', 'GST', 'None', 'Both'].includes(item.OtherTaxType) &&
                                                item.ExportToSoluno === '1'
                                            )
                                            .reduce((sum, item) => sum + parseFloat(item.Amount), 0) + 
                                            parseFloat(gstObject?.GSTOnOtherItems || '0') +
                                            parseFloat(gstObject?.PSTOnOtherItems || '0'))
                                            .toFixed(2)}
                                    </td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            {/* HST Disbursements Card */}
            <Card className="card-list">
                <Card.Header>
                    <h2>Disbursements ({['BC', 'MB', 'SK', 'AB'].includes(appCode) ? 'GST' : 'HST'})</h2>
                </Card.Header>
                <Card.Body>
                    <Table bordered className="table-input">
                        <thead>
                            <tr>
                                <th style={columnStyles.checkbox}></th>
                                <th style={columnStyles.description}>Description</th>
                                <th style={columnStyles.amount}>Amount</th>
                                <th style={columnStyles.code}>Act Code</th>
                            </tr>
                        </thead>
                        <tbody>
                            {expenses
                                .filter(item => item.ItemType === 'Disbursement' && item.IsTaxable === 'Y' && !['PST', 'GST', 'None', 'Both'].includes(item.OtherTaxType))
                                .map(disbursement => (
                                    <tr key={disbursement.Description}>
                                        <td>
                                            <Form.Check 
                                                type="checkbox"
                                                checked={disbursement.ExportToSoluno === '1'}
                                                onChange={() => handleExportToggle(disbursement.Description)}
                                            />
                                        </td>
                                        <td>{disbursement.Description}</td>
                                        <td>{disbursement.Amount}</td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={editableCodeTypes[disbursement.Description] || disbursement.CodeType}
                                                onChange={(e) => handleCodeTypeChange(disbursement.Description, e.target.value)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                        <tfoot>
                            {(appCode === "ON" || appCode === "AB") && (
                                <tr>
                                    <td></td>
                                    <td>{appCode === "ON" ? "HST (13%)" : "GST (5%)"} </td>
                                    <td>{gstObject?.GSTDisbursements || '0.00'}</td>
                                    <td></td>
                                </tr>
                            )}
                            {appCode !== "ON" && appCode !== "AB" && (
                                <tr>
                                    <td></td>
                                    <td>{appCode === "NS" ? "HST (14%)" : "GST (5%)"} </td>
                                    <td>{(appCode === "NS" ? gstObject?.DisbTaxesSK : gstObject?.DisbTaxes) || '0.00'}</td>
                                    <td></td>
                                </tr>
                            )}
                        </tfoot>
                    </Table>
                </Card.Body>
            </Card>

            {/* Non-HST Disbursements Card */}
            <Card className="card-list">
                <Card.Header>
                <h2>Disbursements ({['BC', 'MB', 'SK', 'AB'].includes(appCode) ? 'non-GST' : 'non-HST'})</h2>
                </Card.Header>
                <Card.Body>
                    <Table bordered className="table-input">
                        <thead>
                            <tr>
                                <th style={columnStyles.checkbox}></th>
                                <th style={columnStyles.description}>Description</th>
                                <th style={columnStyles.amount}>Amount</th>
                                <th style={columnStyles.code}>Act Code</th>
                            </tr>
                        </thead>
                        <tbody>
                            {expenses
                                .filter(item => item.ItemType === 'Disbursement' && item.IsTaxable === 'N' && !['PST', 'GST', 'None', 'Both'].includes(item.OtherTaxType))
                                .map(disbursement => (
                                    <tr key={disbursement.Description}>
                                        <td>
                                            <Form.Check 
                                                type="checkbox"
                                                checked={disbursement.ExportToSoluno === '1'}
                                                onChange={() => handleExportToggle(disbursement.Description)}
                                            />
                                        </td>
                                        <td>{disbursement.Description}</td>
                                        <td>{disbursement.Amount}</td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={editableCodeTypes[disbursement.Description] || disbursement.CodeType}
                                                onChange={(e) => handleCodeTypeChange(disbursement.Description, e.target.value)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        </>
    );
};

export default FinancialDetails;
